import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://backend.edumetrix.uk/api';

// Get portal type for storage keys
const portalType = process.env.REACT_APP_PORTAL_TYPE || 'admin';
const STORAGE_KEYS = {
  token: `${portalType}_token`,
  user: `${portalType}_user`
};

// Helper function to get backend base URL (without /api suffix)
export const getBackendBaseUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'https://backend.edumetrix.uk/api';
  // Remove /api suffix if present
  return apiUrl.replace(/\/api$/, '');
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  maxContentLength: 50 * 1024 * 1024,
  maxBodyLength: 50 * 1024 * 1024,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Use portal-specific token
    const token = localStorage.getItem(STORAGE_KEYS.token);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
 
    // A 401 means the stored token is unusable — expired, or signed by a
    // different backend than the one this build points at. Clear it and go back
    // to login in every environment; leaving it in place strands the app on a
    // dead session where every request fails with no way out.
    if (error.response?.status === 401) {
      // Clear portal-specific storage
      localStorage.removeItem(STORAGE_KEYS.token);
      localStorage.removeItem(STORAGE_KEYS.user);
      localStorage.removeItem('userId');
      // Already on a login screen: let the form show its own error instead of
      // reloading the page out from under it.
      if (!window.location.pathname.endsWith('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  validateToken: () => api.get('/auth/validate'),
  getProfile: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  // Admin OTP endpoints
  requestAdminOTP: (email) => api.post('/auth/admin/request-otp', { email }),
  verifyAdminOTP: (email, otp) => api.post('/auth/admin/verify-otp', { email, otp }),
};

export const commonAPI ={
    getAdminDetail: () => {
    return api.get('/common/admin-detail');
  },
}

export const notificationAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllAsRead: () => api.patch('/notifications/mark-all-read'),
  delete: (id) => api.delete(`/notifications/${id}`),
  create: (data) => api.post('/notifications/create', data),
};

export const ringAPI = {
  active: () => api.get('/ring/active'),
  ring: (classId, data) => api.post(`/ring/${classId}`, data),
  acknowledge: (alertId) => api.post(`/ring/${alertId}/acknowledge`, {}),
  classStatus: (classId) => api.get(`/ring/class/${classId}/status`),
};

export const adminMessageAPI = {
  // Get teachers/employees for chat
  getEmployees: (adminId) => {
    return api.get('/admin/message/employee', { params: { adminId } });
  },
  // Get students for chat
  getStudents: (adminId) => {
    return api.get('/admin/message/student', { params: { adminId } });
  },
  // Get individual messages between admin and user
  getIndividualMessages: (adminId, receiverId) => {
    return api.get('/admin/message/admin-individualMessage', {
      params: { adminId, receiverId }
    });
  },
  // Send message
  sendMessage: (data) => {
    return api.post('/admin/message/messages', data);
  },
  // Send message with attachment
  sendMessageWithAttachment: (formData) => {
    return api.post('/admin/message/messages', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Get all conversations
  getConversations: (adminId) => {
    return api.get('/admin/message/conversation', { params: { adminId } });
  },
  // Get all teacher-student chats
  getAllChats: () => {
    return api.get('/admin/message/all-chats');
  },
  // Send broadcast to students
  broadcastToStudents: (senderId, content) => {
    return api.post('/admin/message/broadcast/student', { senderId, content });
  },
  // Send broadcast to students with attachment
  broadcastToStudentsWithAttachment: (formData) => {
    return api.post('/admin/message/broadcast/student', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Send broadcast to teachers
  broadcastToTeachers: (senderId, content) => {
    return api.post('/admin/message/broadcast/employee', { senderId, content });
  },
  // Send broadcast to teachers with attachment
  broadcastToTeachersWithAttachment: (formData) => {
    return api.post('/admin/message/broadcast/employee', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Get broadcast messages
  getBroadcastMessages: (type) => {
    return api.get(`/admin/message/broadcast/${type}`);
  },
};

export const adminAPI = {
  createStudent: (formData) => {
    return api.post('/admin/students', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  createTeacher: (formData) => {
    return api.post('/admin/teachers', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getStudents: (params) => {
    return api.get('/admin/students', { params });
  },
  // Active, non-deleted students grouped by country - every country, unlike the
  // top-10 slice embedded in the finance dashboard stats. Used for the overall
  // (all countries) customer count on the admin dashboard.
  getStudentsByCountry: () => {
    return api.get('/reports/students-by-country');
  },
  // Report export (CSV / PDF / JSON). Goes through the shared client so the
  // admin token and the configured API base URL come from the same place as
  // every other admin call - a hand-rolled fetch here previously read the wrong
  // storage key and used a relative URL that only resolved behind the CRA dev
  // proxy. `blob` keeps the payload intact instead of letting axios parse it.
  exportReport: (reportType, params) =>
    api.get(`/reports/export/${reportType}`, { params, responseType: 'blob' }),
  getStudent: (id) => {
    return api.get(`/admin/students/${id}`);
  },
  toggleStudentStatus: (id) => {
    return api.put(`/admin/students/${id}/toggle-status`);
  },
  permanentDeleteStudent: (id) => {
    return api.delete(`/admin/students/${id}/permanent`);
  },
  deleteAllStudentClasses: (studentId) => {
    return api.delete(`/admin/students/${studentId}/classes/all`);
  },
  getTeachers: (params) => {
    return api.get('/admin/teachers', { params });
  },
  getTeacher: (id) => {
    return api.get(`/admin/teachers/${id}`);
  },
  toggleTeacherStatus: (id) => {
    return api.put(`/admin/teachers/${id}/toggle-status`);
  },
  permanentDeleteTeacher: (id) => {
    return api.delete(`/admin/teachers/${id}/permanent`);
  },
  updateStudent: (id, formData) => api.put(`/admin/students/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateTeacher: (id, formData) => api.put(`/admin/teachers/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  resetStudentPassword: (id, password) => api.post(`/admin/students/${id}/reset-password`, { password }),
  resetTeacherPassword: (id, password) => api.post(`/admin/teachers/${id}/reset-password`, { password }),
  deleteStudent: (id) => api.delete(`/admin/students/${id}`),
  deleteTeacher: (id) => api.delete(`/admin/teachers/${id}`),
  restoreStudent: (id) => api.post(`/admin/students/${id}/restore`),
  restoreTeacher: (id) => api.post(`/admin/teachers/${id}/restore`),
  permanentDeleteStudent: (id) => api.delete(`/admin/students/${id}/permanent`),
  permanentDeleteTeacher: (id) => api.delete(`/admin/teachers/${id}/permanent`),
  createTrialRequest: (data) => api.post('/admin/trial-requests', data),
  getTrialRequests: (params) => api.get('/admin/trial-requests', { params }),
  updateTrialRequest: (id, data) => api.put(`/admin/trial-requests/${id}`, data),
  updateTrialRequestStatus: (id, data) => api.put(`/admin/trial-requests/${id}/status`, data),
  deleteTrialRequest: (id) => api.delete(`/admin/trial-requests/${id}`),
  convertTrialToStudent: (trialId, studentData) => api.post(`/admin/trial-requests/${trialId}/convert`, studentData),
  getSyllabusOptions: (country) => api.get(`/admin/syllabus-options/${country}`),
  updateProfilePicture: (formData) => api.put('/admin/profile/picture', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Class scheduling API methods
  scheduleClass: (data) => api.post('/admin/schedule-class', data),
  getClasses: (params) => {
    return api.get('/admin/classes', { params });
  },
  getClassesCalendar: (params) => {
    return api.get('/admin/classes/calendar', { params });
  },
  updateClass: (id, data) => api.put(`/admin/classes/${id}`, data),
  deleteClass: (id) => api.delete(`/admin/classes/${id}`),
  cancelClass: (id, data) => api.put(`/admin/classes/${id}/cancel`, data),
  getClassStats: (params) => api.get('/admin/classes/stats', { params }),
  // Reschedule request API methods
  getRescheduleRequests: (params) => api.get('/admin/reschedule-requests', { params }),
  getRescheduleRequest: (id) => api.get(`/admin/reschedule-requests/${id}`),
  updateRescheduleRequestStatus: (id, data) => api.put(`/admin/reschedule-requests/${id}/status`, data),
  deleteRescheduleRequest: (id) => api.delete(`/admin/reschedule-requests/${id}`),

  // Settings API methods
  getSettings: () => api.get('/admin/settings'),
  updateAppearance: (data) => api.put('/admin/settings/appearance', data),
  updateNotifications: (data) => api.put('/admin/settings/notifications', data),
  updateProfile: (data) => api.put('/admin/settings/profile', data),
  updatePrivacy: (data) => api.put('/admin/settings/privacy', data),
  updateLanguage: (language) => api.put('/admin/settings/language', { language }),
  updateAdminPreferences: (data) => api.put('/admin/settings/admin-preferences', data),
  updateSystemSettings: (data) => api.put('/admin/settings/system-settings', data),
  updateAllSettings: (data) => api.put('/admin/settings', data),
  resetSettings: () => api.post('/admin/settings/reset'),

  // Group Classes (cohorts)
  listGroupClasses: (params) => api.get('/admin/group-classes', { params }),
  getGroupClass: (id) => api.get(`/admin/group-classes/${id}`),
  createGroupClass: (data) => api.post('/admin/group-classes', data),
  updateGroupClass: (id, data) => api.put(`/admin/group-classes/${id}`, data),
  addGroupClassStudent: (id, studentId) =>
    api.post(`/admin/group-classes/${id}/students`, { studentId }),
  removeGroupClassStudent: (id, studentId) =>
    api.delete(`/admin/group-classes/${id}/students/${studentId}`),
  deleteGroupClass: (id, { force = false } = {}) =>
    api.delete(`/admin/group-classes/${id}${force ? '?force=true' : ''}`),

  // Group class sessions
  listGroupSessions: (id, params) => api.get(`/admin/group-classes/${id}/sessions`, { params }),
  scheduleGroupSession: (id, data) => api.post(`/admin/group-classes/${id}/sessions`, data),
  // Edits ONLY a session's additional display timezones, validated server-side
  // by the same parser the scheduling route uses. Applies to the selected
  // occurrence only — recurring group sessions carry no series id.
  updateGroupSession: (id, sessionId, data) =>
    api.put(`/admin/group-classes/${id}/sessions/${sessionId}`, data),
  // Schedules a NEW session configured like an existing one. Unrelated to the
  // calendar's "Copy Class" button, which only writes to the clipboard.
  duplicateGroupSession: (id, sessionId, data) =>
    api.post(`/admin/group-classes/${id}/sessions/${sessionId}/duplicate`, data),
  cancelGroupSession: (id, sessionId) =>
    api.delete(`/admin/group-classes/${id}/sessions/${sessionId}`),
};

export const classAPI = {
  scheduleClass: (data) => api.post('/classes/schedule', data),
  rescheduleClass: (id, data) => api.put(`/classes/${id}/reschedule`, data),
  getCalendar: (params) => api.get('/classes/calendar', { params }),
  getStudentClasses: (studentId) => api.get(`/classes/student/${studentId}`),
  getTeacherClasses: (teacherId) => api.get(`/classes/teacher/${teacherId}`),
  updateAttendance: (id, data) => api.put(`/classes/${id}/attendance`, data),
  cancelClass: (id) => api.delete(`/classes/${id}`),
};

export const chatAPI = {
  startChat: (data) => {
    return api.post('/chat/start', data);
  },
  sendMessage: (chatId, data) => {
    return api.post(`/chat/${chatId}/message`, data);
  },
  getMyChats: () => {
    return api.get('/chat/my-chats');
  },
  getChatMessages: (chatId, params) => {
    return api.get(`/chat/${chatId}/messages`, { params });
  },
  getAllChats: (params) => {
    return api.get('/chat/admin/all-chats', { params });
  },
  deleteChat: (chatId) => {
    return api.delete(`/chat/${chatId}`);
  },
  uploadAttachment: (formData) => {
    return api.post('/chat/upload-attachment', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  markMessagesAsRead: (chatId, messageIds = null) => {
    return api.put(`/chat/${chatId}/messages/read`, { messageIds });
  },
  getReadStatus: (chatId) => {
    return api.get(`/chat/${chatId}/read-status`);
  },
};

export const financeAPI = {
  getStudentMonthlyReport: (studentId, params) => {
    return api.get(`/finance/student-monthly-report/${studentId}`, { params });
  },
  getTeacherMonthlyReport: (teacherId, params) => {
    return api.get(`/finance/teacher-monthly-report/${teacherId}`, { params });
  },
  generateInvoice: (studentId, data) => {
    return api.post(`/finance/generate-invoice/${studentId}`, data);
  },
  getInvoices: (params) => {
    return api.get('/finance/invoices', { params });
  },
  getInvoice: (id) => {
    return api.get(`/finance/invoices/${id}`);
  },
  updateInvoice: (id, data) => {
    return api.put(`/finance/invoices/${id}`, data);
  },
  updateInvoiceStatus: (id, data) => {
    return api.put(`/finance/invoices/${id}/status`, data);
  },
  deleteInvoice: (id) => {
    return api.delete(`/finance/invoices/${id}`);
  },
  getDashboardStats: () => {
    return api.get('/finance/dashboard-stats');
  },
};

export const teacherAPI = {
  getDashboard: () => {
    return api.get('/teacher/dashboard');
  },
  getClasses: () => {
    return api.get('/teacher/classes');
  },
  updateClass: (id, data) => {
    return api.put(`/teacher/classes/${id}`, data);
  },
  getStudents: () => {
    return api.get('/teacher/students');
  },
  startClass: (id) => api.put(`/teacher/classes/${id}/start`),
  completeClass: (id, data) => api.put(`/teacher/classes/${id}/complete`, data),
  getProfile: () => api.get('/teacher/profile'),
  getMonthlyReport: (params) => api.get('/teacher/monthly-report', { params }),
  // Homework API methods
  getHomework: (params) => {
    return api.get('/teacher/homework', { params });
  },
  getHomeworkById: (id) => api.get(`/teacher/homework/${id}`),
  createHomework: (data) => {
    return api.post('/teacher/homework', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  updateHomework: (id, data) => {
    return api.put(`/teacher/homework/${id}`, data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteHomework: (id) => api.delete(`/teacher/homework/${id}`),
  gradeHomework: (id, data) => api.post(`/teacher/homework/${id}/grade`, data),
  getHomeworkStats: () => api.get('/teacher/homework/stats/overview'),
  // Attendance API methods
  getAttendance: (params) => api.get('/teacher/attendance', { params }),
  saveAttendance: (classId, data) => api.post(`/teacher/attendance/${classId}`, data),
  bulkSaveAttendance: (data) => api.post('/teacher/attendance/bulk', data),
  updateAttendance: (classId, data) => api.put(`/teacher/attendance/${classId}`, data),
  getAttendanceStats: (params) => api.get('/teacher/attendance/stats', { params }),
  getAttendanceStudents: () => api.get('/teacher/attendance/students'),
  // Reports API methods
  getReports: (params) => api.get('/teacher/reports', { params }),
  downloadReport: (params) => api.get('/teacher/reports/download', {
    params,
    responseType: 'blob'
  }),
  // Quick Test API methods
  getQuickTestStudents: () => api.get('/teacher/quicktest/students'),
  createQuickTest: (data) => api.post('/teacher/quicktest', data),
  getQuickTests: (params) => api.get('/teacher/quicktest', { params }),
  getQuickTest: (id) => api.get(`/teacher/quicktest/${id}`),
  updateQuickTest: (id, data) => api.put(`/teacher/quicktest/${id}`, data),
  startQuickTest: (id, data) => api.post(`/teacher/quicktest/${id}/start`, data),
  // Body-less POST would go out with the instance's `Content-Type:
  // application/json` header and an empty payload, which server.js's JSON verify
  // hook rejects as "Invalid JSON" before the route is ever reached. Send {}.
  stopQuickTest: (id) => api.post(`/teacher/quicktest/${id}/stop`, {}),
  getQuickTestResults: (id) => api.get(`/teacher/quicktest/${id}/results`),
  deleteQuickTest: (id) => api.delete(`/teacher/quicktest/${id}`),

  // Settings API methods
  getSettings: () => api.get('/teacher/settings'),
  updateAppearance: (data) => api.put('/teacher/settings/appearance', data),
  updateNotifications: (data) => api.put('/teacher/settings/notifications', data),
  updateProfile: (data) => api.put('/teacher/settings/profile', data),
  updatePrivacy: (data) => api.put('/teacher/settings/privacy', data),
  updateLanguage: (language) => api.put('/teacher/settings/language', { language }),
  updateTeachingPreferences: (data) => api.put('/teacher/settings/teaching-preferences', data),
  updateAllSettings: (data) => api.put('/teacher/settings', data),
  resetSettings: () => api.post('/teacher/settings/reset'),

  // Group classes assigned to the teacher
  getMyGroupClasses: () => api.get('/teacher/group-classes'),

  // Group session attendance
  getGroupAttendance: (classId) => api.get(`/teacher/attendance/group/${classId}`),
  saveGroupAttendance: (classId, entries) =>
    api.post(`/teacher/attendance/group/${classId}`, { entries }),
};

export const studentAPI = {
  getDashboard: () => {
    return api.get('/student/dashboard');
  },
  getClasses: (params) => {
    return api.get('/student/classes', { params });
  },
  getTeachers: () => {
    return api.get('/student/teachers');
  },
  getInvoices: (params) => {
    return api.get('/student/invoices', { params });
  },
  getInvoice: (id) => {
    return api.get(`/student/invoices/${id}`);
  },
  getProfile: () => {
    return api.get('/student/profile');
  },
  getHomework: (params) => {
    return api.get('/student/homework', { params });
  },
  getHomeworkById: (id) => api.get(`/student/homework/${id}`),
  submitHomework: (id, data) => {
    return api.post(`/student/homework/${id}/submit`, data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getHomeworkHistory: (params) => api.get('/student/homework/history/all', { params }),
  getHomeworkSubmission: (id) => api.get(`/student/homework/${id}/submission`),
  getHomeworkStats: () => api.get('/student/homework/stats/overview'),
  joinClass: (classId) => api.get(`/student/join-class/${classId}`),
  getAttendanceReport: (params) => api.get('/student/attendance/report', { params }),
  getAttendanceStats: (params) => api.get('/student/attendance/stats', { params }),
  getAttendanceSummary: () => api.get('/student/attendance/summary'),
  downloadAttendanceReport: (params) => api.get('/student/attendance/download', {
    params,
    responseType: 'blob'
  }),
  getCalendar: (params) => api.get('/student/calendar', { params }),
  // Quick Test API methods
  getQuickTests: (params) => api.get('/student/quicktest', { params }),
  // `intent=view` tells the backend to read the test WITHOUT starting the
  // attempt. Bundles older than POST .../start omit it and keep the legacy
  // start-on-read behaviour, so a stale tab never gets an untimed test.
  getQuickTest: (id) => api.get(`/student/quicktest/${id}`, { params: { intent: 'view' } }),
  // Deliberately starts the attempt; the server stamps startedAt and the
  // countdown runs from there. Idempotent - re-calling resumes the same window.
  // Body must be {} - see the note on stopQuickTest above about the JSON verify
  // hook rejecting a body-less POST.
  startQuickTestAttempt: (id) => api.post(`/student/quicktest/${id}/start`, {}),
  submitQuickTest: (id, data) => api.post(`/student/quicktest/${id}/submit`, data),
  getQuickTestSubmission: (id) => api.get(`/student/quicktest/${id}/submission`),
  getQuickTestHistory: (params) => api.get('/student/quicktest/history/all', { params }),
  // Reschedule request API methods
  createRescheduleRequest: (data) => api.post('/student/reschedule-requests', data),
  getRescheduleRequests: (params) => api.get('/student/reschedule-requests', { params }),

  // Settings API methods
  getSettings: () => api.get('/student/settings'),
  updateAppearance: (data) => api.put('/student/settings/appearance', data),
  updateNotifications: (data) => api.put('/student/settings/notifications', data),
  updateProfile: (data) => api.put('/student/settings/profile', data),
  updatePrivacy: (data) => api.put('/student/settings/privacy', data),
  updateLanguage: (language) => api.put('/student/settings/language', { language }),
  updateAllSettings: (data) => api.put('/student/settings', data),
  resetSettings: () => api.post('/student/settings/reset'),
};

export default api;